import { Close as CloseIcon, Search as SearchIcon } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import { ChangeEvent, memo } from 'react';

import { userLib } from 'entities/user';

import { Country, EUserOnboardingStage, EUserRole } from 'shared/api';
import { LANGUAGE_OPTIONS } from 'shared/config';

type Props = {
  filter: string;
  role: EUserRole | '';
  country: string;
  language: string;
  stage: EUserOnboardingStage | '';
  countries: Country[];
  totalCount: number;
  onFilterChange: (value: string) => void;
  onRoleChange: (value: EUserRole | '') => void;
  onCountryChange: (value: string) => void;
  onLanguageChange: (value: string) => void;
  onStageChange: (value: EUserOnboardingStage | '') => void;
};

export const UsersFilters = memo(
  ({
    filter,
    role,
    country,
    language,
    stage,
    countries,
    totalCount,
    onFilterChange,
    onRoleChange,
    onCountryChange,
    onLanguageChange,
    onStageChange,
  }: Props) => {
    const handleFilterChange = (e: ChangeEvent<HTMLInputElement>) => {
      onFilterChange(e.target.value);
    };

    const handleClearFilter = () => {
      onFilterChange('');
    };

    const handleRoleChange = (e: SelectChangeEvent) => {
      onRoleChange(e.target.value as EUserRole | '');
    };

    const handleLanguageChange = (e: SelectChangeEvent) => {
      onLanguageChange(e.target.value);
    };

    const handleStageChange = (e: SelectChangeEvent) => {
      onStageChange(e.target.value as EUserOnboardingStage | '');
    };

    const selectedCountry = countries.find((c) => c.code === country) ?? null;

    return (
      <Paper sx={{ mb: 2 }}>
        <Box display="flex" alignItems="center" p="8px 16px" gap={2} flexWrap="wrap">
          <Typography variant="h6" sx={{ minWidth: 'fit-content' }}>
            Users control panel
          </Typography>

          <Box display="flex" alignItems="center" gap={2} flex={1} minWidth="300px" flexWrap="wrap">
            <TextField
              sx={{ minWidth: '300px', maxWidth: '500px', flex: 1 }}
              placeholder="Search by name, email, comments..."
              size="small"
              value={filter}
              onChange={handleFilterChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: filter && (
                  <IconButton size="small" onClick={handleClearFilter}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                ),
              }}
            />

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Role</InputLabel>
              <Select value={role} label="Role" onChange={handleRoleChange}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value={EUserRole.User}>{userLib.getUserRoleText(EUserRole.User)}</MenuItem>
                <MenuItem
                  value={EUserRole.Manager}
                  sx={{
                    backgroundColor: userLib.ROLE_COLORS[EUserRole.Manager]?.bg,
                    '&:hover': { backgroundColor: userLib.ROLE_COLORS[EUserRole.Manager]?.hoverBg },
                  }}
                >
                  {userLib.getUserRoleText(EUserRole.Manager)}
                </MenuItem>
                <MenuItem
                  value={EUserRole.Admin}
                  sx={{
                    backgroundColor: userLib.ROLE_COLORS[EUserRole.Admin]?.bg,
                    '&:hover': { backgroundColor: userLib.ROLE_COLORS[EUserRole.Admin]?.hoverBg },
                  }}
                >
                  {userLib.getUserRoleText(EUserRole.Admin)}
                </MenuItem>
              </Select>
            </FormControl>

            <Autocomplete
              size="small"
              sx={{ minWidth: 220 }}
              options={countries}
              value={selectedCountry}
              getOptionLabel={(option) => `${option.name} (${option.code})`}
              isOptionEqualToValue={(option, value) => option.code === value.code}
              onChange={(_, value) => onCountryChange(value?.code ?? '')}
              renderInput={(params) => <TextField {...params} label="Country" />}
            />

            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Language</InputLabel>
              <Select value={language} label="Language" onChange={handleLanguageChange}>
                <MenuItem value="">All</MenuItem>
                {LANGUAGE_OPTIONS.map((lang) => (
                  <MenuItem key={lang.code} value={lang.code}>
                    {lang.name} ({lang.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Stage</InputLabel>
              <Select value={stage} label="Stage" onChange={handleStageChange}>
                <MenuItem value="">All</MenuItem>
                {Object.values(EUserOnboardingStage).map((s) => (
                  <MenuItem key={s} value={s}>
                    {userLib.STAGE_META[s].label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" sx={{ minWidth: 'fit-content' }}>
            <Box display="flex" alignItems="center" gap={1.5}>
              {Object.entries(userLib.ROLE_COLORS as Record<string, { bg: string; hoverBg: string }>).map(
                ([roleKey, c]) => (
                  <Box key={roleKey} display="flex" alignItems="center" gap={0.5}>
                    <Box
                      sx={{
                        width: 14,
                        height: 14,
                        borderRadius: 0.5,
                        backgroundColor: c.bg,
                        border: 1,
                        borderColor: 'divider',
                      }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {userLib.getUserRoleText(roleKey as EUserRole)}
                    </Typography>
                  </Box>
                ),
              )}
            </Box>

            <Typography variant="body2" color="text.secondary">
              {totalCount} {totalCount === 1 ? 'user' : 'users'}
            </Typography>
          </Box>
        </Box>
      </Paper>
    );
  },
);

UsersFilters.displayName = 'UsersFilters';
